import { generatePath } from "react-router-dom";

import { AbsoluteRoute } from "../../core/routes";
import { RouteEntities } from "../../core/routes/relativeRoutes";
import { MyLensesLensCtaType } from "../../gql-generated/types";
import { LensGranularity, type LensInsightsTab, type TabVisibilityMap, lensInsightsTabs } from "../models";

export const INSIGHTS_TIME_RANGE_PARAM = "timeRange";

export interface InsightsLensInput {
    lensCta?: {
        type: MyLensesLensCtaType;
    } | null;
}

export interface InsightsTabVisibilityInput {
    selectedLens?: InsightsLensInput | null;
    selectedTimeRange: LensGranularity;
    isMonetizationHubEnabled: boolean;
}

export interface BuildInsightsTabPathInput {
    orgId?: string | null;
    lensFolderId?: string | null;
    insightsTab: LensInsightsTab;
}

export interface ResolveVisibleInsightsTabInput {
    currentTab?: string | null;
    tabVisibilityMap: TabVisibilityMap;
}

export interface BuildNextInsightsUrlInput {
    orgId?: string | null;
    lensFolderId?: string | null;
    currentTab?: string | null;
    currentSearch: string | URLSearchParams;
    nextTimeRange: LensGranularity;
    selectedLens?: InsightsLensInput | null;
    isMonetizationHubEnabled: boolean;
}

const lensInsightsTabSet = new Set<string>(lensInsightsTabs);

export const isLensInsightsTab = (value?: string | null): value is LensInsightsTab =>
    !!value && lensInsightsTabSet.has(value);

export const hasLensConversion = (selectedLens?: InsightsLensInput | null): boolean =>
    selectedLens?.lensCta?.type !== undefined && selectedLens.lensCta.type !== MyLensesLensCtaType.CtaTypeUnset;

export const getInsightsTabVisibilityMap = ({
    selectedLens,
    selectedTimeRange,
    isMonetizationHubEnabled,
}: InsightsTabVisibilityInput): TabVisibilityMap => {
    const hasSelectedLens = !!selectedLens;
    const isAllTimeSelected = selectedTimeRange === LensGranularity.Lifetime;

    return {
        [RouteEntities.Audience]: !(hasSelectedLens && isAllTimeSelected),
        [RouteEntities.Engagement]: true,
        [RouteEntities.EventInsights]: hasSelectedLens,
        [RouteEntities.Conversion]: hasSelectedLens && hasLensConversion(selectedLens),
        [RouteEntities.Funnel]: hasSelectedLens && !isAllTimeSelected,
        [RouteEntities.Monetization]: !isMonetizationHubEnabled,
    };
};

export const getFirstVisibleInsightsTab = (tabVisibilityMap: TabVisibilityMap): LensInsightsTab =>
    lensInsightsTabs.find((tab) => tabVisibilityMap[tab]) ?? RouteEntities.Engagement;

export const resolveVisibleInsightsTab = ({
    currentTab,
    tabVisibilityMap,
}: ResolveVisibleInsightsTabInput): LensInsightsTab => {
    if (isLensInsightsTab(currentTab) && tabVisibilityMap[currentTab]) {
        return currentTab;
    }

    return getFirstVisibleInsightsTab(tabVisibilityMap);
};

export const buildInsightsTabPath = ({ orgId, lensFolderId, insightsTab }: BuildInsightsTabPathInput): string => {
    if (orgId && lensFolderId) {
        return generatePath(AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB, {
            orgId,
            lensFolderId,
            insightsTab,
        });
    }

    if (orgId) {
        return generatePath(AbsoluteRoute.ORG_INSIGHTS_TAB, { orgId, insightsTab });
    }

    return generatePath(AbsoluteRoute.INSIGHTS_TAB, { insightsTab });
};

export const buildNextInsightsUrl = ({
    orgId,
    lensFolderId,
    currentTab,
    currentSearch,
    nextTimeRange,
    selectedLens,
    isMonetizationHubEnabled,
}: BuildNextInsightsUrlInput): string => {
    const searchParams = new URLSearchParams(currentSearch);
    searchParams.set(INSIGHTS_TIME_RANGE_PARAM, nextTimeRange);

    const tabVisibilityMap = getInsightsTabVisibilityMap({
        selectedLens,
        selectedTimeRange: nextTimeRange,
        isMonetizationHubEnabled,
    });
    const insightsTab = resolveVisibleInsightsTab({ currentTab, tabVisibilityMap });
    const pathname = buildInsightsTabPath({ orgId, lensFolderId, insightsTab });
    const nextSearch = searchParams.toString();

    return nextSearch ? `${pathname}?${nextSearch}` : pathname;
};
